// Kohtaamiset (Teams / sarjaottelut). docs/features/teams.md §4.4 and Phase D.
//
// KEEP THIS FILE FREE OF REACT AND FIREBASE, like utils/teamformats.js and utils/teams.js.
//
// The one thing to understand before changing anything here: a tie stores NO times and NO
// courts (§4.4). Admins move and cancel rubbers through the ordinary reservation path, so a
// reservation can reach state 'cancelled' without the tie ever being told. Every function below
// therefore takes the JOINED reservations as an argument and derives from them - never from
// `rubbers.length`, which counts intentions rather than bookings.

import { flattenRubbers, findRow, rubberTypeLabel } from './teamformats'

// Two states, not three. There is no decline: the manager mails an opponent the links for
// SEVERAL candidate dates and the opponent picks by clicking the one that suits them. A date
// they do not want is simply a link they never click, so "kieltäytyi" is not an answer anyone
// can give - it is the absence of one, which is what 'pending' already means.
export const CONFIRMATION_STATES = [
  { const: 'pending', title: 'Odottaa vahvistusta' },
  { const: 'confirmed', title: 'Vahvistettu' },
]

export const confirmationState = (tie) => {
  const state = tie && tie.confirmation && tie.confirmation.state
  return state || 'pending'
}

export const confirmationLabel = (tie) => {
  const state = confirmationState(tie)
  const found = CONFIRMATION_STATES.find((s) => s.const === state)
  return found ? found.title : state
}

export const isCancelled = (tie) => !!(tie && tie.state === 'cancelled')

export const openTies = (ties) => (Array.isArray(ties) ? ties.filter((t) => t && !isCancelled(t)) : [])

// A rubber counts as booked only if its reservation is actually there and alive. A rubber whose
// reservation an admin cancelled is 'gone': it still sits in `rubbers[]` (nothing tells the tie),
// so counting the array would report a court the venue no longer holds.
export const rubberStatus = (rubber, reservationsById) => {
  if (!rubber || !rubber.reservationid) return 'missing'
  const reservation = reservationsById && reservationsById[rubber.reservationid]
  if (!reservation) return 'missing'
  if (reservation.state === 'cancelled') return 'cancelled'
  return 'booked'
}

// booked = courts the venue is actually holding for this tie right now.
// expected = what the format calls for, so the venue can see 2/4 at a glance.
// gone     = rubbers the tie thinks it has but does not, which is exactly the drift an admin
//            moving bookings around creates and the only way the venue finds out.
export const rubberTally = (tie, format, reservationsById) => {
  const rubbers = tie && Array.isArray(tie.rubbers) ? tie.rubbers : []
  let booked = 0
  let gone = 0
  rubbers.forEach((r) => {
    const status = rubberStatus(r, reservationsById)
    if (status === 'booked') booked += 1
    else gone += 1
  })
  return { booked, gone, expected: flattenRubbers(format).length }
}

export const tallyLabel = (tally) => {
  const base = tally.booked + '/' + tally.expected + ' varattu'
  return tally.gone ? base + ' (' + tally.gone + ' peruttu)' : base
}

// Every reservation id a set of ties points at, deduplicated - what the container fetches so it
// can join. Deliberately includes ids whose reservation may be gone: finding that out is the
// point (see `gone` above).
export const reservationIdsOf = (ties) => {
  const ids = []
  ;(Array.isArray(ties) ? ties : []).forEach((tie) => {
    ;(tie && Array.isArray(tie.rubbers) ? tie.rubbers : []).forEach((r) => {
      if (r && r.reservationid && ids.indexOf(r.reservationid) < 0) ids.push(r.reservationid)
    })
  })
  return ids
}

// One row per rubber for the tie detail: which format row it fills, and when and where it
// actually is according to the reservation. Times come from the reservation every single time -
// §4.4's whole point is that nothing here is cached.
export const rubberRows = (tie, format, reservationsById) => {
  const rubbers = tie && Array.isArray(tie.rubbers) ? tie.rubbers : []
  return rubbers.map((r) => {
    const row = findRow(format, r && r.rubberid)
    const reservation = r && r.reservationid ? (reservationsById || {})[r.reservationid] : null
    const group = reservation && Array.isArray(reservation.resourcegroups) ? reservation.resourcegroups[0] : null
    return {
      reservationid: r && r.reservationid,
      rubberid: r && r.rubberid,
      // A row the venue deleted from the format after this rubber was booked. The booking is
      // real and must still render; only its label is unknown (§4.2).
      typelabel: row ? rubberTypeLabel(row.type) : '(poistettu ottelu)',
      duration: row ? row.duration : null,
      status: rubberStatus(r, reservationsById),
      start: group ? group.start || null : null,
      end: group ? group.end || null : null,
      resources: group && Array.isArray(group.resources) ? group.resources : [],
    }
  })
}

export const resourceNames = (resources) =>
  (Array.isArray(resources) ? resources : [])
    .map((res) => (res && (res.displayName || res.name)) || '')
    .filter((n) => n)
    .join(', ')

// --- quota and the spare ---------------------------------------------------
//
// The spare is NOT nominated up front and there is NO `extra` field. It is simply whichever tie
// is still pending once the confirmed count has reached `homematches`.
//
// That falls out of how acceptance actually works: the manager reserves one more date than the
// team has home fixtures and mails each opponent the links for the candidate dates. An opponent
// picks by clicking the link that suits them, so the date nobody clicked is the spare - and
// which one that turns out to be is not knowable in advance, by anyone. Asking the manager to
// nominate it would be asking them to guess and then holding them to the guess.

export const tiesOfTeam = (ties, teamid) => openTies(ties).filter((t) => t.teamid === teamid)

export const confirmedCount = (ties, teamid) =>
  tiesOfTeam(ties, teamid).filter((t) => confirmationState(t) === 'confirmed').length

export const pendingTies = (ties, teamid) =>
  tiesOfTeam(ties, teamid).filter((t) => confirmationState(t) === 'pending')

// Dead inventory: the team has all the confirmed home ties it needs, yet is still holding courts
// on a date no opponent chose. Every opponent who was going to click has clicked, so nothing
// more will happen to these by itself.
//
// The manager gets 24 hours to release them, after which the backend does it (Phase E). This
// function is what both the venue's list and that job read, so keep it the one definition.
export const staleSpares = (team, ties) => {
  if (!team || !team.homematches) return []
  const confirmed = confirmedCount(ties, team.id)
  if (confirmed < team.homematches) return []
  return pendingTies(ties, team.id)
}

// --- the two-choice rule ---------------------------------------------------
//
// This is WHY the quota is `homematches + 1`, and it is a league rule, not slack: the home team
// must offer every opponent at least two dates to choose between. The +1 is exactly the minimum
// that guarantees it.
//
// Opponents claim one at a time, so with h fixtures and h+1 dates the k-th opponent to answer
// sees (h + 1) - (k - 1) dates, and the last one sees 2. Four opponents over five dates: 5, 4,
// 3, 2. Take one date away and the last opponent has no choice at all, which breaks the rule
// the venue is bound by - so nothing may quietly "optimise" the spare away.
export const MIN_CHOICES = 2

// Does the team still have enough unclaimed dates for every remaining opponent to get two?
//
// `remaining` opponents claiming one each need `remaining + 1` unclaimed dates, by the same
// arithmetic: the last of them must still see two. Fewer than that and some opponent will be
// handed a single date - usually because a rubber or a whole tie was cancelled mid-season,
// which is the case worth catching, since by then the manager has already mailed the links.
export const choiceStatus = (team, ties) => {
  if (!team || !team.homematches) return null
  const confirmed = confirmedCount(ties, team.id)
  const unclaimed = pendingTies(ties, team.id).length
  const remaining = Math.max(0, team.homematches - confirmed)
  const needed = remaining ? remaining + 1 : 0
  return { confirmed, unclaimed, remaining, needed, ok: unclaimed >= needed }
}

// How many dates the next opponent to answer will be able to choose between. Below MIN_CHOICES
// the rule is already broken for them, not merely at risk.
export const choicesForNextOpponent = (team, ties) => {
  const status = choiceStatus(team, ties)
  if (!status || !status.remaining) return null
  return status.unclaimed
}

export const SPARE_GRACE_HOURS = 24

// When the grace period started: the moment the LAST confirmation landed, because that is the
// moment the spare became surplus. Returns null while the team still needs confirmations, and
// for a tie set carrying no usable timestamp - never a guessed time, since this drives an
// automatic cancellation.
export const spareGraceStartedAt = (team, ties) => {
  if (!staleSpares(team, ties).length) return null
  const times = tiesOfTeam(ties, team.id)
    .filter((t) => confirmationState(t) === 'confirmed')
    .map((t) => t.confirmation && t.confirmation.confirmedat)
    .map((v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null))
    .filter((d) => d && !isNaN(d.getTime()))
  if (!times.length) return null
  return new Date(Math.max.apply(null, times))
}

// Hours left before the backend releases the spare. Negative means the window has passed and
// the release is overdue - shown rather than hidden, because an overdue release is a sign the
// Phase E job is not running.
export const spareHoursLeft = (team, ties, now) => {
  const started = spareGraceStartedAt(team, ties)
  if (!started) return null
  const elapsed = ((now ? now.getTime() : Date.now()) - started.getTime()) / 3600000
  return Math.round((SPARE_GRACE_HOURS - elapsed) * 10) / 10
}

// Pending ties whose date is inside the next `days`. The opponent has not answered and the date
// is coming - the venue phones the manager. Dates are plain 'YYYY-MM-DD' strings (§4.4), so they
// compare lexically and no timezone enters into it.
export const pendingSoon = (ties, todayiso, days) => {
  if (!todayiso) return []
  const horizon = addDays(todayiso, days)
  return openTies(ties)
    .filter((t) => confirmationState(t) === 'pending')
    .filter((t) => t.date && t.date >= todayiso && t.date <= horizon)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

// Deliberately not moment: this file stays dependency-free so the Phase E guard can share it.
export const addDays = (iso, days) => {
  const parts = String(iso).split('-')
  const d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])))
  if (isNaN(d.getTime())) return iso
  d.setUTCDate(d.getUTCDate() + (Number(days) || 0))
  return d.toISOString().slice(0, 10)
}
